<template>
  <div :class="$options.name">
    <v-dialog v-model="dialog" width="1024px">
      <div class="dialog-container">
        <div v-if="isLoading" class="loading-overlay">
          <v-progress-circular
            :size="50"
            width="7"
            color="primary"
            indeterminate
          />
        </div>

        <map-component v-if="dialog" @created="onMapCreated" />

        <div v-if="modelLoaded" class="model-settings">
          <div class="setting">
            <div class="font-weight-medium">Размер модели</div>
            <div class="actions">
              <v-btn
                color="primary"
                x-small
                @click="incrementModelScale(0.1e-8)"
              >
                Больше
              </v-btn>
              <v-btn
                color="primary"
                x-small
                @click="decrementModelScale(0.1e-8)"
              >
                Меньше
              </v-btn>
            </div>
          </div>

          <div class="setting">
            <div class="font-weight-medium">Положение относительно оси X</div>
            <div class="actions">
              <v-btn color="primary" x-small @click="incrementTranslateX(0.000001)">
                Больше
              </v-btn>
              <v-btn color="primary" x-small @click="decrementTranslateX(0.000001)">
                Меньше
              </v-btn>
            </div>
          </div>

          <div class="setting">
            <div class="font-weight-medium">Положение относительно оси Y</div>
            <div class="actions">
              <v-btn color="primary" x-small @click="incrementTranslateY(0.000001)">
                Больше
              </v-btn>
              <v-btn color="primary" x-small @click="decrementTranslateY(0.000001)">
                Меньше
              </v-btn>
            </div>
          </div>

          <div class="setting">
            <div class="font-weight-medium">Положение относительно оси Z</div>
            <div class="actions">
              <v-btn color="primary" x-small @click="incrementTranslateZ(0.000001)">
                Больше
              </v-btn>
              <v-btn color="primary" x-small @click="decrementTranslateZ(0.000001)">
                Меньше
              </v-btn>
            </div>
          </div>

          <div class="setting">
            <div class="font-weight-medium">Вращение относительно оси X</div>
            <div class="actions">
              <v-btn color="primary" x-small @click="incrementRotateX(0.01)">Больше</v-btn>
              <v-btn color="primary" x-small @click="decrementRotateX(0.01)">Меньше</v-btn>
            </div>
          </div>

          <div class="setting">
            <div class="font-weight-medium">Вращение относительно оси Y</div>
            <div class="actions">
              <v-btn color="primary" x-small @click="incrementRotateY(0.01)">Больше</v-btn>
              <v-btn color="primary" x-small @click="decrementRotateY(0.01)">Меньше</v-btn>
            </div>
          </div>

          <div class="setting">
            <div class="font-weight-medium">Вращение относительно оси Z</div>
            <div class="actions">
              <v-btn color="primary" x-small @click="incrementRotateZ(0.01)">Больше</v-btn>
              <v-btn color="primary" x-small @click="decrementRotateZ(0.01)">Меньше</v-btn>
            </div>
          </div>
        </div>

        <div class="help">
          <span v-if="!modelLoaded" class="font-weight-bold">
            Шаг 1. Выберите местоположение модели на карте
          </span>
          <span v-else-if="modelLoaded" class="font-weight-bold">
            Шаг 2. Управляйте проекцией модели на карте с помощью элементов управления
            <v-btn class="mt-2" @click="onSave">Сохранить изменения</v-btn>
          </span>
        </div>
      </div>

      <div v-if="modelLoaded" class="inputs">
        <v-text-field
          v-model="modelSettings.scale"
          label="Размер модели"
          class="mx-2"
        />
        <v-text-field
          v-model="modelSettings.translateX"
          label="X - Ось"
          class="mx-2"
        />
        <v-text-field
          v-model="modelSettings.translateY"
          label="Y - Ось"
          class="mx-2"
        />
        <v-text-field
          v-model="modelSettings.translateZ"
          label="Z - Ось"
          class="mx-2"
        />
        <v-text-field
          v-model="modelSettings.rotateX"
          label="X - Вращение"
          class="mx-2"
        />
        <v-text-field
          v-model="modelSettings.rotateY"
          label="Y - Вращение"
          class="mx-2"
        />
        <v-text-field
          v-model="modelSettings.rotateZ"
          label="Z - Вращение"
          class="mx-2"
        />
      </div>
    </v-dialog>
  </div>
</template>

<script>
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { LegacyGLTFLoader } from '@/utils/three-modules/LegacyGLTFLoader';
import isEmpty from 'lodash/isEmpty';
import MapComponent from '@/components/map';
import DialogBusMixin from '../../mixins/dialog-bus-mixin';

const fromLL = (lon, lat) => {
  const extent = 20037508.34;

  const x = (lon * extent) / 180;
  let y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180);
  y = (y * extent) / 180;

  return [(x + extent) / (2 * extent), 1 - (y + extent) / (2 * extent)];
};

export const createDefaultSettings = ({ lat, lng }) => {
  const translate = fromLL(lng, lat);

  return {
    translateX: translate[0],
    translateY: translate[1],
    translateZ: -0.000004,
    rotateX: Math.PI / 2,
    rotateY: 0,
    rotateZ: 0,
    scale: 3.799043220338983e-8,
  };
};

export default {
  name: 'map-model-editor-dialog',
  components: { MapComponent },
  mixins: [DialogBusMixin],
  model: {
    event: 'change',
    prop: 'settings',
  },
  props: {
    settings: [Array, Object],
    model: Object,
  },
  data: () => ({
    isLoading: false,
    modelLoaded: false,
    modelCenter: {},
    modelSettings: {},
  }),
  watch: {
    modelSettings: {
      deep: true,
      handler: 'updateSettings',
    },
    modelCenter: {
      deep: true,
      handler: 'updateSettings',
    },
  },
  methods: {
    async onMapCreated(instance) {
      // eslint-disable-next-line no-param-reassign
      instance.getCanvas().style.cursor = 'crosshair';

      instance.on('click', (event) => this.onMapClick(event, instance));

      if (!isEmpty(this.settings)) {
        this.modelSettings = {
          ...this.settings,
        };
        const { lng, lat } = this.settings.center;

        await this.initModel(instance);
        instance.flyTo({ center: [lng, lat], zoom: 16 });
      }
    },

    async onMapClick(event, instance) {
      this.modelCenter = event.lngLat;

      const defaultSettings = createDefaultSettings(this.modelCenter);
      if (isEmpty(this.settings)) {
        this.modelSettings = defaultSettings;
      } else {
        const { translateX, translateY } = defaultSettings;
        this.modelSettings = {
          ...this.settings,
          translateX,
          translateY,
        };
      }

      await this.initModel(instance);
    },

    async initModel(instance) {
      if (!this.modelLoaded) {
        this.isLoading = true;
        await this.renderModel(instance);
        this.modelLoaded = true;
        this.isLoading = false;
      }
    },

    renderModel(instance) {
      return new Promise((resolve, reject) => {
        const layer = {
          id: 'model-3d-layer',
          source: 'composite',
          type: 'custom',
          renderingMode: '3d',
          onAdd: (map, gl) => {
            this.camera = new THREE.Camera();
            this.scene = new THREE.Scene();

            const directionalLight = new THREE.DirectionalLight(0xffffff);
            directionalLight.position.set(30, 50, 20).normalize();
            this.scene.add(directionalLight);

            const directionalLight2 = new THREE.DirectionalLight(0xffffff);
            directionalLight2.position.set(-30, -50, 20).normalize();
            this.scene.add(directionalLight2);

            const directionalLight3 = new THREE.DirectionalLight(0xffffff);
            directionalLight3.position.set(-30, 50, 20).normalize();
            this.scene.add(directionalLight3);

            const directionalLight4 = new THREE.DirectionalLight(0xffffff);
            directionalLight4.position.set(30, -50, 20).normalize();
            this.scene.add(directionalLight4);

            const axesHelper = new THREE.AxesHelper(500);
            this.scene.add(axesHelper);

            const newLoader = new GLTFLoader();
            newLoader.load(
              this.model.path,
              gltf => {
                this.scene.add(gltf.scene);
                resolve();
              },
              null,
              () => {
                const oldLoader = new LegacyGLTFLoader();
                oldLoader.load(
                  this.url,
                  (gltf) => {
                    this.scene.add(gltf.scene);
                    resolve();
                  },
                  null,
                  (e) => {
                    reject(e);
                  },
                );
              },
            );

            this.renderer = new THREE.WebGLRenderer({
              canvas: map.getCanvas(),
              context: gl,
            });

            this.renderer.autoClear = false;
          },
          render: (gl, matrix) => {
            const rotationX = new THREE.Matrix4().makeRotationAxis(
              new THREE.Vector3(1, 0, 0),
              this.modelSettings.rotateX,
            );
            const rotationY = new THREE.Matrix4().makeRotationAxis(
              new THREE.Vector3(0, 1, 0),
              this.modelSettings.rotateY,
            );
            const rotationZ = new THREE.Matrix4().makeRotationAxis(
              new THREE.Vector3(0, 0, 1),
              this.modelSettings.rotateZ,
            );

            const m = new THREE.Matrix4().fromArray(matrix);
            const l = new THREE.Matrix4()
              .makeTranslation(
                this.modelSettings.translateX,
                this.modelSettings.translateY,
                this.modelSettings.translateZ,
              )
              .scale(
                new THREE.Vector3(
                  this.modelSettings.scale,
                  -this.modelSettings.scale,
                  this.modelSettings.scale,
                ),
              )
              .multiply(rotationX)
              .multiply(rotationY)
              .multiply(rotationZ);

            this.camera.projectionMatrix.elements = matrix;
            this.camera.projectionMatrix = m.multiply(l);
            this.renderer.state.reset();
            this.renderer.render(this.scene, this.camera);
            instance.triggerRepaint();
          },
        };

        if (!this.modelLoaded) {
          instance.addLayer(layer);
        }
      });
    },

    incrementModelScale(value) {
      this.modelSettings.scale = this.modelSettings.scale + value;
    },
    decrementModelScale(value) {
      this.modelSettings.scale = this.modelSettings.scale - value;
    },

    incrementRotateX(value) {
      this.modelSettings.rotateX = this.modelSettings.rotateX + value;
    },
    decrementRotateX(value) {
      this.modelSettings.rotateX = this.modelSettings.rotateX - value;
    },

    incrementRotateY(value) {
      this.modelSettings.rotateY = this.modelSettings.rotateY + value;
    },
    decrementRotateY(value) {
      this.modelSettings.rotateY = this.modelSettings.rotateY - value;
    },

    incrementRotateZ(value) {
      this.modelSettings.rotateZ = this.modelSettings.rotateZ + value;
    },
    decrementRotateZ(value) {
      this.modelSettings.rotateZ = this.modelSettings.rotateZ - value;
    },

    incrementTranslateX(value) {
      this.modelSettings.translateX = this.modelSettings.translateX + value;
    },
    decrementTranslateX(value) {
      this.modelSettings.translateX = this.modelSettings.translateX - value;
    },

    incrementTranslateY(value) {
      this.modelSettings.translateY = this.modelSettings.translateY + value;
    },
    decrementTranslateY(value) {
      this.modelSettings.translateY = this.modelSettings.translateY - value;
    },

    incrementTranslateZ(value) {
      this.modelSettings.translateZ = this.modelSettings.translateZ + value;
    },
    decrementTranslateZ(value) {
      this.modelSettings.translateZ = this.modelSettings.translateZ - value;
    },

    updateSettings() {
      const data = {
        ...this.modelSettings,
      };

      if (!isEmpty(this.modelCenter)) {
        data.center = this.modelCenter;
      }

      this.$emit('change', data);

      return data;
    },

    onSave() {
      const data = this.updateSettings();
      this.$emit('save', data);
    },
  },
};
</script>

<style lang="scss" scoped>

.dialog-container {
  position: relative;
  height: 600px;
  width: 100%;
}

.loading-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(#000, .3);
  z-index: 100;
}

.help {
  position: absolute;
  bottom: 8px;
  left: 8px;
  width: 250px;
  color: white;
  padding: 8px;
  background-color: rgba(#000, .3);
  z-index: 1111;
}

.model-settings {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  padding: 8px;
  width: 210px;
  background-color: rgba(#000, .3);
  color: #fff;
  z-index: 111;

  .setting {
    margin-bottom: 16px;

    .title {
      margin-bottom: 8px;
    }

    .actions {
      * + * {
        margin-left: 8px;
      }
    }
  }
}

.inputs {
  position: relative;
  z-index: 10;
  background: white;
  margin: 0;
  padding: 0;
  display: flex;
}
</style>
